import { getRequestByCommunityId } from '@/api/requests';
import { Box, Button, Container, Divider, Paper, Stack, Typography, useTheme } from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { PaperPlaneTilt as PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/ssr';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import type { Request } from '@/types/v2/request';
import { paths } from '@/paths';
import { useUser } from '@/hooks/v2/use-user';
import { isArcRequest } from '@/constants/request-status-flows';
import { Seo } from '@/components/seo';

import { ArcReviewSection } from './components/arc-details-card';
import { RequestAttachments } from './components/request-attachment-card';
import { RequestConversation } from './components/request-conversation-card';
import { RequestDescription, RequestSidebar } from './components/request-details-card';
import { RequestHeader } from './components/request-header';
import { RequestQuotes } from './components/request-quotes-card';

interface RequestResponse {
  request?: Request | null;
}

export default function RequestPageV2() {
  const { community } = useUser();
  const { requestId } = useParams();
  const theme = useTheme();
  const { data, isLoading, error } = useQuery<RequestResponse>({
    queryKey: ['request', community?.id, requestId],
    queryFn: async () => {
      const response = await getRequestByCommunityId(community?.id, requestId, {
        params: {
          include: 'unit.owners,unit.currentResidents,unit.unitType,files,requestStatus,requestType,createdBy,assignedTo',
        },
      });
      return response ? response : { request: null };
    },
    enabled: !!community?.id && !!requestId,
    retry: false,
  });

  const request = data?.request;

  if (!isLoading && error?.message === 'Request not found') {
    return (
      <>
        <Seo title="Request Not Found" />
        <Box
          component="main"
          sx={{ flexGrow: 1, py: 3, backgroundColor: 'background.paper', minHeight: '100vh' }}
        >
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, textAlign: 'center' }}>
              <PaperPlaneTiltIcon color={theme.palette.text.disabled} size={64} style={{ marginBottom: 24 }} />
              <Typography gutterBottom sx={{ fontWeight: 600 }} variant="h5">
                Request not found
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }} variant="body2">
                This request doesn't exist or you don't have access to it.
              </Typography>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={() => { window.history.back(); }} variant="outlined">Go Back</Button>
                <Button href={paths.requests.index} variant="contained">All Requests</Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      </>
    );
  }

  const showArc = isArcRequest(request?.request_type?.name) && !!request;

  return (
    <>
      <Seo title={request?.summary ?? 'Request'} />
      <Box component="main" sx={{ flexGrow: 1, backgroundColor: 'background.paper', minHeight: '100vh' }}>
        {/* Header strip */}
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: { xs: 2, md: 4 }, py: 3 }}>
          <Container disableGutters maxWidth="xl">
            <RequestHeader error={error} isLoading={isLoading} request={request} />
          </Container>
        </Box>

        {/* Body */}
        <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Grid container spacing={4}>
            {/* ── Left column: narrative ── */}
            <Grid md={8} xs={12}>
              <Paper
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
              >
                {/* Description */}
                <Box sx={{ p: 3 }}>
                  <RequestDescription request={request} isLoading={isLoading} />
                </Box>

                {/* ARC Review (conditional) */}
                {showArc && (
                  <>
                    <Divider />
                    <Box sx={{ p: 3 }}>
                      <ArcReviewSection request={request!} />
                    </Box>
                  </>
                )}

                {/* Activity / Conversation */}
                {request?.conversation_id && (
                  <>
                    <Divider />
                    <Box sx={{ p: 3 }}>
                      <RequestConversation conversationId={request.conversation_id} requestId={request.id} />
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>

            {/* ── Right column: sidebar ── */}
            <Grid md={4} xs={12}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  position: { md: 'sticky' },
                  top: { md: 24 },
                }}
              >
                {/* Details + Property + Residents */}
                <Box sx={{ p: 3 }}>
                  <RequestSidebar request={request} isLoading={isLoading} />
                </Box>

                {/* Attachments */}
                <Divider />
                <Box sx={{ p: 3 }}>
                  <RequestAttachments request={request!} isLoading={isLoading} error={error} />
                </Box>

                {/* Quotes (self-guarded by role) */}
                <Divider />
                <Box sx={{ p: 3 }}>
                  <RequestQuotes request={request} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}
